import { Box, Skeleton, Stack, Typography } from '@mui/material';
import React, { ReactNode, useMemo } from 'react';
import { getFullName } from '../../../../../person/person';
import EditablePanel, { EditablePanelProps } from '../../../../components/EditablePanel';
import EllipsisText from '../../../../components/EllipsisText';
import { useBrowserOrAccountLocale } from '../../../../hooks/useBrowserOrAccountLocale';
import I18n from '../../../../i18n/I18n';
import { toLocaleDateString } from '../../../../utils/dateTime';
import { APPROVAL_EDITABLE_PANEL_CONTENT_SX } from '../../../constants';
import { useReviewApprovalContext } from '../../hooks/useReviewApprovalContext';
import CreateApprovalFormEditablePanelHeader from '../CreateApprovalFormEditablePanelHeader';

const headerActionsSx = { flex: 1 };

export type ApprovalNotesViewProps = Pick<EditablePanelProps, 'isCollapsable' | 'defaultCollapsed'>;

export default function ApprovalNotesView({ isCollapsable = true, defaultCollapsed = false }: ApprovalNotesViewProps) {
    const { approvalData, isFetchingApprovalData } = useReviewApprovalContext();
    const locale = useBrowserOrAccountLocale();

    const stepsWithComment = useMemo(
        () => approvalData?.steps?.filter((step) => step.comment?.trim()) || [],
        [approvalData?.steps]
    );

    let notesContent: ReactNode = null;
    if (isFetchingApprovalData) {
        notesContent = Array.from({ length: 2 }).map((_, skeletonIndex) => (
            <Skeleton key={skeletonIndex} variant="rounded" width="100%" height={60} />
        ));
    } else if (stepsWithComment.length === 0) {
        notesContent = (
            <Typography
                color="textSecondary"
                sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}
            >
                <I18n token="approval.notes.noNotes" />
            </Typography>
        );
    } else {
        notesContent = stepsWithComment.map((step) => (
            <Box
                key={step.id}
                sx={(theme) => ({
                    px: 1.5,
                    py: 1,
                    border: 1,
                    borderStyle: 'solid',
                    borderColor: theme.palette.mode === 'dark' ? `grey.700` : `grey.400`,
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75
                })}
            >
                <EllipsisText text={step.comment} />
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                    <span>{step.occurredAt ? toLocaleDateString(step.occurredAt, true, locale) : ''}</span>
                    <span>{getFullName(step.contact.name)}</span>
                </Typography>
            </Box>
        ));
    }

    return (
        <EditablePanel
            Header={<CreateApprovalFormEditablePanelHeader titleToken="approval.notes.header" />}
            isCollapsable={isCollapsable}
            defaultCollapsed={defaultCollapsed}
            headerActionsSx={headerActionsSx}
        >
            <Stack sx={[APPROVAL_EDITABLE_PANEL_CONTENT_SX, { gap: 1.5 }]}>{notesContent}</Stack>
        </EditablePanel>
    );
}
